import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'
import { gql, useQuery } from '@apollo/client'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import { makeStyles } from '@material-ui/core/styles'
import { gtmEvent } from 'lib/gtm'
import {
  PROMO_CALLOUT_BORDER_RADIUS,
  CALLOUT_BACKGROUND,
  CALLOUT_FOREGROUND,
} from 'lib/form-field-styles'
import CloseIcon from 'components/material-symbol/icons/Close'
import EventIcon from 'components/material-symbol/icons/Event'
import CelebrationIcon from 'components/material-symbol/icons/Celebration'
import EventBusyIcon from 'components/material-symbol/icons/EventBusy'
import CalendarAddOnIcon from 'components/material-symbol/icons/CalendarAddOn'
import CircularHighlightedIcon from 'components/circular-highlighted-icon'
import OneTimeSlotDialog from 'pages/pga-coach/settings/availability/availability-tab/one-time-slot-dialog'
import { CreateEventQuickStartOverlay } from 'pages/pga-coach/bookings/events/create-event-form/form-sections/create-event-quick-start'
import useCreateEventQuickStart from 'pages/pga-coach/bookings/events/use-create-event-quick-start'
import { buildCoachFastBookUrlFromCalendarSlot } from 'lib/pga-coach/coach-book-student'

const GET_GROUP_EVENTS_LIMIT = gql`
  query GetGroupEventsLimit {
    currentUser {
      attributes {
        ... on Coach {
          freeTierLimitsReached
        }
      }
    }
  }
`

const useStyles = makeStyles(theme => ({
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: theme.zIndex.modal,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      alignItems: 'flex-start',
      overflowY: 'auto',
    },
  },
  modal: {
    position: 'relative',
    width: '100%',
    maxWidth: 1040,
    backgroundColor: theme.palette.common.white,
    borderRadius: PROMO_CALLOUT_BORDER_RADIUS,
    padding: theme.spacing(4),
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(3),
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
  },
  closeButton: {
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 1,
    color: theme.palette.text.primary,
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.3,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
    paddingRight: theme.spacing(7),
  },
  subtitle: {
    fontSize: '1rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    lineHeight: 1.5,
  },
  cardsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  cardColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: 0,
  },
  card: {
    width: '100%',
    minHeight: 200,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3, 2.5, 2.5),
    borderRadius: PROMO_CALLOUT_BORDER_RADIUS,
    border: '1px solid #D5DDE3',
    backgroundColor: theme.palette.common.white,
    cursor: 'pointer',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      borderColor: '#9AA8B3',
    },
  },
  cardTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontSize: '1.125rem',
    fontWeight: 600,
    lineHeight: 1.3,
    color: '#00234B',
    textAlign: 'center',
    textTransform: 'none',
  },
  cardDescription: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
}))

const OptionCard = ({ icon, title, description, onClick, classes }) => (
  <Box className={classes.cardColumn}>
    <Box
      className={classes.card}
      onClick={onClick}
      role="button"
      tabIndex={0}
      data-testid={`schedule-add-option-${title
        .toLowerCase()
        .replace(/\s+/g, '-')}`}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onClick()
        }
      }}
    >
      <CircularHighlightedIcon
        icon={icon}
        size={56}
        iconSize={28}
        highlightColor={CALLOUT_BACKGROUND}
        iconColor={CALLOUT_FOREGROUND}
      />
      <Typography className={classes.cardTitle}>{title}</Typography>
      <Typography className={classes.cardDescription}>{description}</Typography>
    </Box>
  </Box>
)

const ScheduleAddToCalendarDialog = ({
  open,
  onClose,
  slotContext,
  facilities,
  defaultFacilityId,
  existingOverrides,
  onAvailabilityChange,
}) => {
  const classes = useStyles()
  const history = useHistory()
  const { data: limitsData, loading: limitsLoading } = useQuery(
    GET_GROUP_EVENTS_LIMIT,
    { fetchPolicy: 'cache-first' },
  )
  const isGroupEventsLimitReached = Boolean(
    limitsData?.currentUser?.attributes?.freeTierLimitsReached?.includes(
      'GROUP_EVENTS',
    ),
  )
  const createEventQuickStart = useCreateEventQuickStart({
    canBuildOwnEvent: !isGroupEventsLimitReached,
  })
  const [blockedTimeOpen, setBlockedTimeOpen] = useState(false)
  const [openTimeOpen, setOpenTimeOpen] = useState(false)
  const [pendingSlot, setPendingSlot] = useState({ day: null, hour: null })

  useEffect(() => {
    if (open && slotContext?.day) {
      setPendingSlot({
        day: slotContext.day,
        hour: slotContext.hour ?? null,
      })
    }
  }, [open, slotContext])

  const captureSlotContext = () => {
    if (slotContext?.day) {
      setPendingSlot({
        day: slotContext.day,
        hour: slotContext.hour ?? null,
      })
    }
  }

  const day = pendingSlot.day
  const hour = pendingSlot.hour
  const defaultDate = day || null
  const defaultStartTime =
    day && hour != null
      ? day.set({ hour, minute: 0, second: 0, millisecond: 0 })
      : null
  const closePicker = () => {
    onClose()
  }

  const closeAvailabilityDialogs = () => {
    setBlockedTimeOpen(false)
    setOpenTimeOpen(false)
    setPendingSlot({ day: null, hour: null })
  }

  const handleBookStudent = () => {
    gtmEvent({
      event: 'formSubmit',
      formCategory: 'book-session',
      formAction: 'clicked-book-session-button',
    })
    closePicker()
    const { pathname, search } = history.location
    history.push(
      buildCoachFastBookUrlFromCalendarSlot(pathname, day, hour, search),
    )
  }

  const handleCreateGroupEvent = () => {
    closePicker()
    createEventQuickStart.open()
  }

  const handleBlockedTime = () => {
    captureSlotContext()
    setBlockedTimeOpen(true)
    closePicker()
  }

  const handleOpenTime = () => {
    captureSlotContext()
    setOpenTimeOpen(true)
    closePicker()
  }

  const handleAvailabilitySuccess = () => {
    onAvailabilityChange?.()
    closeAvailabilityDialogs()
  }

  const openTimeReady = existingOverrides !== undefined

  const options = [
    {
      icon: EventIcon,
      title: 'Book a student',
      description: 'Schedule a lesson at this time.',
      onClick: handleBookStudent,
    },
    {
      icon: CelebrationIcon,
      title: 'Create group event',
      description: 'Set up a new group event.',
      onClick: handleCreateGroupEvent,
    },
    {
      icon: EventBusyIcon,
      title: 'Blocked time',
      description: 'Mark this time as not bookable.',
      onClick: handleBlockedTime,
    },
    openTimeReady
      ? {
        icon: CalendarAddOnIcon,
        title: 'Open time',
        description: 'Add one-time bookable hours.',
        onClick: handleOpenTime,
      }
      : null,
  ].filter(Boolean)

  const showPicker = open && !blockedTimeOpen && !openTimeOpen

  return (
    <>
      {showPicker ? (
        <Box
          className={classes.overlay}
          role="presentation"
          onClick={closePicker}
          data-testid="schedule-add-to-calendar-dialog"
        >
          <Box
            className={classes.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="schedule-add-to-calendar-title"
            onClick={event => event.stopPropagation()}
          >
            <IconButton
              className={classes.closeButton}
              aria-label="Close"
              onClick={closePicker}
            >
              <CloseIcon />
            </IconButton>

            <Typography
              id="schedule-add-to-calendar-title"
              className={classes.title}
              component="h2"
            >
              What do you want to add to your calendar?
            </Typography>
            <Typography className={classes.subtitle}>
              Pick what you&apos;re scheduling. You can adjust the details next.
            </Typography>

            <Box className={classes.cardsRow}>
              {options.map(option => (
                <OptionCard key={option.title} {...option} classes={classes} />
              ))}
            </Box>
          </Box>
        </Box>
      ) : null}

      {createEventQuickStart.isOpen && !limitsLoading ? (
        <CreateEventQuickStartOverlay {...createEventQuickStart.overlayProps} />
      ) : null}

      <OneTimeSlotDialog
        facilities={facilities}
        onSuccess={handleAvailabilitySuccess}
        triggerless
        isOpen={blockedTimeOpen}
        onClose={closeAvailabilityDialogs}
        initialType="block"
        defaultFacilityId={defaultFacilityId}
        defaultDate={defaultDate}
        defaultStartTime={defaultStartTime}
      />
      <OneTimeSlotDialog
        facilities={facilities}
        onSuccess={handleAvailabilitySuccess}
        triggerless
        isOpen={openTimeOpen}
        onClose={closeAvailabilityDialogs}
        initialType="open"
        existingOverrides={existingOverrides}
        defaultFacilityId={defaultFacilityId}
        defaultDate={defaultDate}
        defaultStartTime={defaultStartTime}
      />
    </>
  )
}

ScheduleAddToCalendarDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  slotContext: PropTypes.shape({
    day: PropTypes.object,
    hour: PropTypes.number,
  }),
  facilities: PropTypes.arrayOf(PropTypes.object),
  defaultFacilityId: PropTypes.string,
  existingOverrides: PropTypes.arrayOf(PropTypes.object),
  onAvailabilityChange: PropTypes.func,
}

export default ScheduleAddToCalendarDialog
